import { UrlParams } from '@vueuse/core';
import { logError } from '@/src/utils/loggers';

// Route prefix for directly opening a study from a DICOMWeb (Orthanc) server,
// e.g. https://3dview.anphumedical.vn/idcachup/<StudyInstanceUID>
const STUDY_ROUTE_PREFIX =
  import.meta.env.VITE_STUDY_ROUTE_PREFIX || 'idcachup';

/**
 * Extracts a StudyInstanceUID from a path of the form
 * `<base>/<prefix>/<StudyInstanceUID>` (e.g. `/idcachup/1.2.3.4`).
 *
 * Returns undefined when the current path does not match the prefix.
 */
export const getStudyUidFromPath = (
  pathname: string = window.location.pathname
): string | undefined => {
  const parts = pathname.split('/').filter(Boolean);
  const idx = parts.indexOf(STUDY_ROUTE_PREFIX);
  if (idx !== -1 && parts[idx + 1]) {
    return decodeURIComponent(parts[idx + 1]);
  }
  return undefined;
};

type ParsedUrlParams = {
  urls?: string[];
  names?: string[];
  config?: string[];
  save?: string | string[];
};

const isValidUrl = (str: string) => {
  try {
    return !!new URL(str.trim(), window.location.href);
  } catch {
    return false;
  }
};

const splitAndClean = (str: string) =>
  str
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean);

const parseUrlArray = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((v) => parseUrlArray(v));
  }

  const trimmed = value.trim();

  if (!trimmed) return [];

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    return splitAndClean(trimmed.slice(1, -1));
  }

  return [trimmed];
};

export const normalizeUrlParams = (rawParams: UrlParams): ParsedUrlParams => {
  const normalized: ParsedUrlParams = {};

  if (rawParams.urls) {
    const urls = parseUrlArray(rawParams.urls);
    const validUrls = urls.filter((url) => {
      const isValid = isValidUrl(url);
      if (!isValid) {
        logError(new Error(`Invalid URL in urls parameter: ${url}`));
      }
      return isValid;
    });

    if (validUrls.length > 0) {
      normalized.urls = validUrls;
    }
  }

  if (rawParams.names) {
    normalized.names = parseUrlArray(rawParams.names);
  }

  if (rawParams.config) {
    const configs = parseUrlArray(rawParams.config);
    const validConfigs = configs.filter((url) => {
      const isValid = isValidUrl(url);
      if (!isValid) {
        logError(new Error(`Invalid URL in config parameter: ${url}`));
      }
      return isValid;
    });

    if (validConfigs.length > 0) {
      normalized.config = validConfigs;
    }
  }

  if (rawParams.save) {
    normalized.save = rawParams.save;
  }

  return normalized;
};
